import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, MessageCircle, MapPin, CheckCircle2, XCircle, RefreshCw, Expand, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl, waLink, km, priceLabel, STATUS_LABEL, STATUS_COLOR } from "@/lib/api";
import BackButton from "@/components/BackButton";
import { PaymentNote } from "@/components/PriceInput";
import { useServiceMap } from "@/lib/data";
import { ServiceIcon } from "@/lib/icons";
import { Avatar, Rating, StarInput } from "@/components/Bits";
import { brl, featuredPrice, withDiscount } from "@/lib/prices";

function Searching({ req, onCancel }) {
  return (
    <div className="po-card text-center" data-testid="searching-panel">
      <div className="relative mx-auto my-6 flex h-36 w-36 items-center justify-center">
        <span className="po-radar-ring absolute h-24 w-24 rounded-full bg-orange-400" />
        <span className="po-radar-ring absolute h-24 w-24 rounded-full bg-orange-400" style={{ animationDelay: "1.1s" }} />
        <span className="relative flex h-20 w-20 items-center justify-center rounded-full bg-orange-500 text-white"><MapPin className="h-10 w-10" /></span>
      </div>
      <h2 className="font-display text-2xl font-extrabold text-slate-900">Buscando prestador perto de você...</h2>
      <p className="mt-2 text-lg text-slate-700">Oferecemos seu pedido a um prestador de cada vez, do mais perto para o mais longe.</p>
      <p className="mt-2 text-base text-slate-600" data-testid="offered-count">Prestadores consultados: {req.offered_count}</p>
      <button onClick={onCancel} className="po-btn-outline mt-5 w-full" data-testid="cancel-request-button"><XCircle className="h-6 w-6" /> Cancelar pedido</button>
    </div>
  );
}

function NoProvider({ req, act }) {
  const bigger = Math.min(50, (req.radius_km || 15) + 10);
  return (
    <div className="po-card" data-testid="no-provider-panel">
      <h2 className="font-display text-2xl font-extrabold text-slate-900">Ainda não encontramos um prestador.</h2>
      <p className="mt-2 text-lg text-slate-700">Quer tentar de novo ou ampliar a busca?</p>
      <div className="mt-5 grid gap-3">
        <button onClick={() => act("retry", {})} className="po-btn-primary" data-testid="retry-request-button"><RefreshCw className="h-6 w-6" /> Tentar de novo</button>
        <button onClick={() => act("retry", { radius_km: bigger })} className="po-btn-dark" data-testid="expand-request-button"><Expand className="h-6 w-6" /> Ampliar busca para {bigger} km</button>
        <button onClick={() => act("cancel")} className="po-btn-outline" data-testid="cancel-request-button">Cancelar pedido</button>
      </div>
    </div>
  );
}

const place = (r) => [r.neighborhood, r.city].filter(Boolean).join(", ");

// Pre-filled WhatsApp texts: any missing piece is dropped, never left as a placeholder.
const clientWaText = (r, off = 0, price = null) => {
  const what = [r.service_name && `de ${r.service_name}`, place(r) && `em ${place(r)}`].filter(Boolean).join(" ");
  const seen = price
    ? `Vi no site: ${price.name} ${brl(price.value)}${off > 0 ? `, com ${off}% de desconto (${brl(withDiscount(price.value, off))})` : ""}.`
    : off > 0 && `Vi no site que você dá ${off}% de desconto no orçamento.`;
  return ["Olá! Vim pelo Prestadores Online" + (what ? ` e preciso ${what}.` : "."),
    r.client_name && `Meu nome é ${r.client_name.trim()}.`, seen, "Você pode me atender?"].filter(Boolean).join(" ");
};

// Discount and price are read at click time so the message always carries what is valid right now.
async function openClientWhatsApp(e, req) {
  e.preventDefault();
  let p = req.provider;
  const win = window.open("", "_blank");
  try { p = { ...p, ...(await api.get(`/providers/${p.id}`)).data }; } catch (err) { /* keep last known */ }
  const url = waLink(req.provider.whatsapp, clientWaText(req, Number(p.discount_pct) || 0, featuredPrice(p)));
  if (win) win.location.href = url; else window.location.href = url;
}
const providerWaText = (r) => {
  const what = [r.service_name && `de ${r.service_name}`, place(r) && `em ${place(r)}`].filter(Boolean).join(" ");
  return `Olá! Sou do Prestadores Online e vi o seu pedido${what ? ` ${what}` : ""}. Posso te atender?`;
};

function Accepted({ req, act }) {
  const p = req.provider;
  return (
    <div className="po-card" data-testid="accepted-panel">
      <p className="flex items-center gap-2 text-xl font-bold text-green-700"><CheckCircle2 className="h-7 w-7" /> Prestador encontrado!</p>
      <div className="mt-4 flex gap-4">
        <Avatar photo={p.photo} name={p.name} />
        <div>
          <p className="font-display text-2xl font-bold text-slate-900" data-testid="accepted-provider-name">{p.name}</p>
          <div className="mt-1 flex flex-wrap gap-3"><Rating avg={p.rating_avg} count={p.rating_count} /><span className="text-base font-semibold text-orange-700">{km(p.distance_km)}</span></div>
        </div>
      </div>
      {req.status === "aceito" && (
        <a href={waLink(p.whatsapp, clientWaText(req, p.discount_pct, featuredPrice(p)))} onClick={(e) => openClientWhatsApp(e, req)} target="_blank" rel="noreferrer"
          className="po-btn-green mt-5 w-full min-h-[68px] text-xl" data-testid="provider-whatsapp-button">
          <MessageCircle className="h-7 w-7" /> Falar no WhatsApp
        </a>
      )}
      {req.status === "aceito" && req.provider_done && (
        <div className="mt-4 rounded-2xl bg-orange-50 p-4">
          <p className="text-lg text-slate-800">O prestador marcou o serviço como concluído.</p>
          <button onClick={() => act("confirm")} className="po-btn-primary mt-3 w-full" data-testid="confirm-done-button"><CheckCircle2 className="h-6 w-6" /> Confirmar que foi concluído</button>
        </div>
      )}
      {req.status === "aceito" && <button onClick={() => act("cancel")} className="po-btn-outline mt-3 w-full" data-testid="cancel-request-button">Cancelar pedido</button>}
    </div>
  );
}

function Review({ req, onSaved }) {
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");
  if (req.review) {
    return <div className="po-card" data-testid="review-done"><p className="text-xl font-bold">Sua avaliação: {"★".repeat(req.review.stars)}</p>{req.review.comment && <p className="mt-2 text-lg">{req.review.comment}</p>}</div>;
  }
  const send = async () => {
    try { await api.post(`/requests/${req.id}/review`, { stars, comment }); toast.success("Obrigado pela avaliação!"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="po-card" data-testid="review-form">
      <h2 className="font-display text-2xl font-bold text-slate-900">Como foi o serviço?</h2>
      <div className="mt-3"><StarInput value={stars} onChange={setStars} /></div>
      <textarea className="po-input mt-4 min-h-[110px] py-3" placeholder="Conte como foi (opcional)" value={comment} onChange={(e) => setComment(e.target.value)} maxLength={500} data-testid="review-comment-input" />
      <button disabled={!stars} onClick={send} className="po-btn-primary mt-4 w-full" data-testid="review-submit-button">Enviar avaliação</button>
    </div>
  );
}

function ProviderJob({ req, reload }) {
  const done = async () => {
    try { await api.post(`/requests/${req.id}/done`); toast.success("Marcado como concluído. Aguardando o cliente confirmar."); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="po-card" data-testid="provider-job-panel">
      {req.client_whatsapp ? (
        <>
          <p className="text-lg text-slate-700">Cliente</p>
          <p className="font-display text-2xl font-bold text-slate-900" data-testid="client-name">{req.client_name}</p>
          <a href={waLink(req.client_whatsapp, providerWaText(req))}
            target="_blank" rel="noreferrer" className="po-btn-green mt-4 w-full min-h-[68px] text-xl" data-testid="client-whatsapp-button">
            <MessageCircle className="h-7 w-7" /> Falar com o cliente no WhatsApp
          </a>
          {req.status === "aceito" && !req.provider_done && <button onClick={done} className="po-btn-dark mt-3 w-full" data-testid="provider-done-button"><CheckCircle2 className="h-6 w-6" /> Marcar como concluído</button>}
          {req.provider_done && req.status === "aceito" && <p className="mt-3 text-lg text-slate-700">Aguardando o cliente confirmar a conclusão.</p>}
        </>
      ) : (
        <p className="text-lg text-slate-700">Este pedido está aguardando sua resposta ou já foi aceito por outro prestador.</p>
      )}
    </div>
  );
}

export default function RequestStatus() {
  const { id } = useParams();
  const qc = useQueryClient();
  const services = useServiceMap();
  const { data: req, isLoading, error } = useQuery({
    queryKey: ["request", id],
    queryFn: () => api.get(`/requests/${id}`).then((r) => r.data),
    refetchInterval: (q) => (["buscando", "aguardando", "aceito"].includes(q.state.data?.status) ? 3000 : false),
  });
  const reload = () => qc.invalidateQueries({ queryKey: ["request", id] });
  const act = async (kind, body) => {
    if (kind === "cancel" && !window.confirm("Deseja mesmo cancelar o pedido?")) return;
    try { await api.post(`/requests/${id}/${kind}`, body); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (isLoading) return <div className="flex justify-center py-24"><Loader2 className="h-10 w-10 animate-spin text-orange-500" /></div>;
  if (error) return <p className="py-24 text-center text-xl" data-testid="request-error">{errMsg(error)}</p>;
  const svc = services[req.service_slug];

  return (
    <div className="mx-auto max-w-2xl space-y-5 px-4 py-6 sm:px-6">
      <BackButton to={req.role === "provider" ? "/perfil" : "/meus-pedidos"} />
      <div className="flex items-center gap-4">
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-500 text-white"><ServiceIcon icon={svc?.icon} className="h-8 w-8" /></span>
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">{svc?.name || req.service_name}</h1>
          <p className="text-lg text-slate-700">{req.neighborhood}, {req.city}</p>
        </div>
        <span className={`ml-auto rounded-full px-3 py-1 text-base font-bold ${STATUS_COLOR[req.status]}`} data-testid="request-status-badge">{STATUS_LABEL[req.status]}</span>
      </div>
      {req.role === "provider" ? <ProviderJob req={req} reload={reload} /> : (
        <>
          {req.status === "buscando" && <Searching req={req} onCancel={() => act("cancel")} />}
          {req.status === "aguardando" && (
            <div className="po-card text-center" data-testid="waiting-panel">
              <h2 className="font-display text-2xl font-extrabold text-slate-900">Aguardando prestador</h2>
              <p className="mt-2 text-lg text-slate-700">Ninguém desta profissão está disponível agora. Seu pedido fica guardado por até 2 horas e aparece para o primeiro prestador que entrar no site.</p>
              <button onClick={() => act("cancel")} className="po-btn-outline mt-5 w-full" data-testid="cancel-request-button"><XCircle className="h-6 w-6" /> Cancelar pedido</button>
            </div>
          )}
          {req.status === "sem_prestador" && <NoProvider req={req} act={act} />}
          {req.provider && <Accepted req={req} act={act} />}
          {req.status === "concluido" && <Review req={req} onSaved={reload} />}
          {req.status === "cancelado" && <div className="po-card text-lg" data-testid="cancelled-panel">Este pedido foi cancelado. <Link to="/" className="font-semibold text-orange-600 underline">Fazer novo pedido</Link></div>}
        </>
      )}
      <div className="po-card">
        <p className="text-lg font-semibold text-slate-900">{req.role === "provider" ? "Pedido" : "Seu pedido"}</p>
        <p className="mt-1 text-xl font-bold text-orange-600" data-testid="request-price">{priceLabel(req)}</p>
        {req.description && <p className="mt-1 text-lg text-slate-700" data-testid="request-description">{req.description}</p>}
        {req.photos?.length > 0 && <div className="mt-3 flex gap-2">{req.photos.map((p) => <img key={p} src={fileUrl(p)} alt="Foto" className="h-24 w-24 rounded-xl object-cover" />)}</div>}
        <PaymentNote className="mt-3" />
      </div>
      {req.events?.length > 0 && (
        <div className="po-card" data-testid="request-events">
          <p className="flex items-center gap-2 text-lg font-semibold text-slate-900"><ClipboardList className="h-6 w-6 text-orange-500" /> Andamento</p>
          <ul className="mt-3 space-y-2">
            {[...req.events].reverse().map((ev, i) => (
              <li key={i} className="flex gap-3 text-base text-slate-700">
                <span className="w-14 shrink-0 font-semibold tabular-nums text-slate-500">{new Date(ev.at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</span>{ev.text}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
